import { useId, useMemo, type ReactNode } from 'react';
import { FloatingLabel, Form } from 'react-bootstrap';

import { InfoTooltip } from 'components/base/InfoTooltip';

export interface EditSelectOption<V> {
  readonly key: string | number;
  readonly value: V;
  readonly label: string;
}

export interface EditSelectGroup<V> {
  readonly key: string | number;
  readonly label: string;
  readonly options: readonly EditSelectOption<V>[];
}

export type EditSelectOptionList<V> = readonly (EditSelectOption<V> | EditSelectGroup<V>)[];

interface EditSelectProps<V> {
  /** Floating label. Omit to render a bare control (the caller supplies its own label, e.g. via `Field`). */
  readonly label?: string;
  readonly value: V;
  readonly onChange: (value: V) => void;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  readonly options: EditSelectOptionList<V>;
  /**
   * Explanatory text in a tooltip beside the control (same pattern as
   * `EditBoolean.info`). Only shown when `label` is set.
   */
  readonly info?: ReactNode;
}

const isGroup = <V,>(item: EditSelectOption<V> | EditSelectGroup<V>): item is EditSelectGroup<V> => 'options' in item;

/**
 * Dropdown wrapper around `Form.Select` (wrapped in a `FloatingLabel`
 * when `label` is given; a bare control otherwise). Options carry
 * their own `key` (used as both the React key and the DOM `<option
 * value>`) and a `value` that's handed back to `onChange` on
 * selection. Groups are valid items in the options list — flat
 * `<option>`s and `<optgroup>`s can interleave.
 *
 * Nullability is the caller's concern: include `null` (or any sentinel
 * "no selection" value) in `V` and prepend an option that holds it.
 * Using `key: ''` for that option keeps HTML `required` validation
 * working (the browser treats `value=""` as no selection).
 */
export const EditSelect = <V,>({ label, value, onChange, mandatory = false, disabled = false, options, info }: EditSelectProps<V>) => {
  const id = useId();
  // Flatten once per render so both the controlled-value lookup
  // (V → DOM key) and the change handler (DOM key → V) share the
  // same key/value pairs.
  const flatOptions = useMemo(() => {
    const out: EditSelectOption<V>[] = [];
    for (const item of options) {
      if (isGroup(item)) out.push(...item.options);
      else out.push(item);
    }
    return out;
  }, [options]);

  const keyToValue = useMemo(() => new Map(flatOptions.map((o) => [String(o.key), o.value])), [flatOptions]);

  const currentDomKey = flatOptions.find((o) => o.value === value)?.key ?? '';

  const control = (
    <Form.Select
      value={String(currentDomKey)}
      onChange={(e) => {
        const k = e.target.value;
        if (keyToValue.has(k)) onChange(keyToValue.get(k) as V);
      }}
      required={mandatory}
      disabled={disabled}
    >
      {options.map((item) =>
        isGroup(item) ? (
          <optgroup key={item.key} label={item.label}>
            {item.options.map((opt) => (
              <option key={opt.key} value={String(opt.key)}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        ) : (
          <option key={item.key} value={String(item.key)}>
            {item.label}
          </option>
        )
      )}
    </Form.Select>
  );

  if (!label) {
    return control;
  }

  // The id goes on the labelled branch only: a bare control takes its id from an enclosing `Form.Group` (e.g. `Field`), whose label points at it.
  const labeled = (
    <FloatingLabel controlId={id} label={mandatory ? `${label} *` : label}>
      {control}
    </FloatingLabel>
  );
  if (info === undefined) {
    return labeled;
  }

  // align-items-center: floating-label selects are taller than a checkbox row;
  // center keeps the badge mid-control rather than on the label baseline.
  return (
    <div className="d-flex align-items-center gap-2">
      <div className="flex-grow-1">{labeled}</div>
      <InfoTooltip describes={label}>{info}</InfoTooltip>
    </div>
  );
};
